import { create } from 'zustand'
import { toast } from 'react-hot-toast'
import taskService from '@services/taskService'

/**
 * Task Store
 *
 * Zustand store for managing task state (Phase 1: My Tasks & Approvals)
 * Tasks are checklist items viewed from the perspective of assigned users
 */
export const useTaskStore = create((set, get) => ({
	// State
	myTasks: [],
	myApprovals: [],
	allTasks: [],
	selectedTask: null,
	loading: false,
	error: null,
	statistics: {
		not_started: 0,
		pending_assignment: 0,
		pending: 0,
		in_progress: 0,
		pending_review: 0,
		completed: 0,
		rejected: 0,
		overdue: 0,
		total: 0,
	},
	filters: {
		search: '',
		project_id: null,
		workflow_status: 'all',
		sortBy: 'due_date',
		sortOrder: 'ASC',
	},
	pagination: {
		page: 1,
		limit: 10,
		total: 0,
		pages: 0,
	},
	approvalsPagination: {
		page: 1,
		limit: 10,
		total: 0,
		pages: 0,
	},
	allTasksPagination: {
		page: 1,
		limit: 10,
		total: 0,
		pages: 0,
	},

	// Actions - Fetch My Tasks
	fetchMyTasks: async (params = {}) => {
		set({ loading: true, error: null })

		try {
			const { filters, pagination } = get()
			// Determine workflow_status: use params first, then filters
			let workflowStatus = params.workflow_status
			if (workflowStatus === undefined && filters.workflow_status && filters.workflow_status !== 'all') {
				workflowStatus = filters.workflow_status
			}

			const queryParams = {
				project_id: params.project_id || filters.project_id || undefined,
				workflow_status: workflowStatus || undefined,
				search: params.search || filters.search || undefined,
				page: params.page || pagination.page,
				limit: params.limit || pagination.limit,
			}

			// Remove undefined values
			Object.keys(queryParams).forEach(
				(key) => queryParams[key] === undefined && delete queryParams[key]
			)

			const response = await taskService.getMyTasks(queryParams)

			if (response.success) {
				set({
					myTasks: response.data || [],
					pagination: response.pagination || get().pagination,
					loading: false,
				})
			} else {
				throw new Error(response.error || 'Failed to fetch my tasks')
			}
		} catch (error) {
			console.error('Error fetching my tasks:', error)
			set({ error: error.message, loading: false })
			toast.error('Failed to fetch your tasks')
		}
	},

	// Actions - Fetch My Approvals
	fetchMyApprovals: async (params = {}) => {
		set({ loading: true, error: null })

		try {
			const { filters, approvalsPagination } = get()
			const queryParams = {
				project_id: params.project_id || filters.project_id || undefined,
				search: params.search || filters.search || undefined,
				page: params.page || approvalsPagination.page,
				limit: params.limit || approvalsPagination.limit,
			}

			// Remove undefined values
			Object.keys(queryParams).forEach(
				(key) => queryParams[key] === undefined && delete queryParams[key]
			)

			const response = await taskService.getMyApprovals(queryParams)

			if (response.success) {
				set({
					myApprovals: response.data || [],
					approvalsPagination: response.pagination || get().approvalsPagination,
					loading: false,
				})
			} else {
				throw new Error(response.error || 'Failed to fetch my approvals')
			}
		} catch (error) {
			console.error('Error fetching my approvals:', error)
			set({ error: error.message, loading: false })
			toast.error('Failed to fetch pending approvals')
		}
	},

	// Actions - Fetch All Tasks (Admin only)
	fetchAllTasks: async (params = {}) => {
		set({ loading: true, error: null })

		try {
			const { filters, allTasksPagination } = get()
			// Determine workflow_status: use params first, then filters
			let workflowStatus = params.workflow_status
			if (workflowStatus === undefined && filters.workflow_status && filters.workflow_status !== 'all') {
				workflowStatus = filters.workflow_status
			}

			const queryParams = {
				project_id: params.project_id || filters.project_id || undefined,
				workflow_status: workflowStatus || undefined,
				assigned_to: params.assigned_to || undefined,
				search: params.search || filters.search || undefined,
				page: params.page || allTasksPagination.page,
				limit: params.limit || allTasksPagination.limit,
			}

			// Remove undefined values
			Object.keys(queryParams).forEach(
				(key) => queryParams[key] === undefined && delete queryParams[key]
			)

			const response = await taskService.getAll(queryParams)

			if (response.success) {
				set({
					allTasks: response.data || [],
					allTasksPagination: response.pagination || get().allTasksPagination,
					loading: false,
				})
			} else {
				throw new Error(response.error || 'Failed to fetch all tasks')
			}
		} catch (error) {
			console.error('Error fetching all tasks:', error)
			set({ error: error.message, loading: false })
			toast.error('Failed to fetch tasks')
		}
	},

	// Actions - Fetch Task Statistics
	fetchStatistics: async () => {
		try {
			const response = await taskService.getStatistics()

			if (response.success) {
				set({ statistics: response.data || get().statistics })
				return response.data
			} else {
				throw new Error(response.error || 'Failed to fetch statistics')
			}
		} catch (error) {
			console.error('Error fetching statistics:', error)
			return get().statistics
		}
	},

	// Actions - Fetch Task Details
	fetchTaskById: async (id) => {
		set({ loading: true, error: null })

		try {
			const response = await taskService.getById(id)

			if (response.success) {
				set({ selectedTask: response.data, loading: false })
				return response.data
			} else {
				throw new Error(response.error || 'Failed to fetch task details')
			}
		} catch (error) {
			console.error('Error fetching task:', error)
			set({ error: error.message, loading: false })
			toast.error('Failed to fetch task details')
			return null
		}
	},

	// Actions - Start Task
	startTask: async (id) => {
		set({ loading: true, error: null })

		try {
			const response = await taskService.start(id)

			if (response.success) {
				// Refresh my tasks
				await get().fetchMyTasks()
				await get().fetchStatistics()

				toast.success('Task started successfully')
				return true
			} else {
				throw new Error(response.error || 'Failed to start task')
			}
		} catch (error) {
			console.error('Error starting task:', error)
			set({ error: error.message, loading: false })
			toast.error(error.message || 'Failed to start task')
			return false
		}
	},

	// Actions - Submit Task
	submitTask: async (id, data = {}) => {
		set({ loading: true, error: null })

		try {
			const response = await taskService.submit(id, data)

			if (response.success) {
				// Refresh my tasks
				await get().fetchMyTasks()
				await get().fetchStatistics()

				toast.success('Task submitted for review successfully')
				return true
			} else {
				throw new Error(response.error || 'Failed to submit task')
			}
		} catch (error) {
			console.error('Error submitting task:', error)
			set({ error: error.message, loading: false })
			toast.error(error.message || 'Failed to submit task')
			return false
		}
	},

	// Actions - Accept Task (approver accepts - moves to pending_review)
	acceptTask: async (id) => {
		set({ loading: true, error: null })

		try {
			const response = await taskService.accept(id)

			if (response.success) {
				// Refresh approvals and statistics
				await get().fetchMyApprovals()
				await get().fetchStatistics()

				toast.success('Task accepted successfully')
				return response
			} else {
				throw new Error(response.error || 'Failed to accept task')
			}
		} catch (error) {
			console.error('Error accepting task:', error)
			set({ error: error.message, loading: false })
			toast.error(error.message || 'Failed to accept task')
			return false
		}
	},

	// Actions - Complete Task (final step - marks workflow as 100% complete)
	completeTask: async (id, data = {}) => {
		set({ loading: true, error: null })

		try {
			const response = await taskService.complete(id, data)

			if (response.success) {
				// Refresh approvals and statistics
				await get().fetchMyApprovals()
				await get().fetchStatistics()

				toast.success('Task completed! Workflow is now 100% complete.')
				return true
			} else {
				throw new Error(response.error || 'Failed to complete task')
			}
		} catch (error) {
			console.error('Error completing task:', error)
			set({ error: error.message, loading: false })
			toast.error(error.message || 'Failed to complete task')
			return false
		}
	},

	// Actions - Approve Task (alias for accept - backward compatibility)
	approveTask: async (id, data = {}) => {
		set({ loading: true, error: null })

		try {
			const response = await taskService.approve(id, data)

			if (response.success) {
				// Refresh approvals and statistics
				await get().fetchMyApprovals()
				await get().fetchStatistics()

				toast.success('Task approved successfully')
				return true
			} else {
				throw new Error(response.error || 'Failed to approve task')
			}
		} catch (error) {
			console.error('Error approving task:', error)
			set({ error: error.message, loading: false })
			toast.error(error.message || 'Failed to approve task')
			return false
		}
	},

	// Actions - Reject Task
	rejectTask: async (id, remarks) => {
		set({ loading: true, error: null })

		try {
			const response = await taskService.reject(id, { remarks })

			if (response.success) {
				// Refresh approvals and statistics
				await get().fetchMyApprovals()
				await get().fetchStatistics()

				toast.success('Task rejected and returned to previous assignee')
				return true
			} else {
				throw new Error(response.error || 'Failed to reject task')
			}
		} catch (error) {
			console.error('Error rejecting task:', error)
			set({ error: error.message, loading: false })
			toast.error(error.message || 'Failed to reject task')
			return false
		}
	},

	// Actions - Assign Task Manually
	assignTaskManually: async (id, userId) => {
		set({ loading: true, error: null })

		try {
			const response = await taskService.assignManually(id, { user_id: userId })

			if (response.success) {
				// Refresh all tasks (for admins) or my tasks
				await get().fetchAllTasks()
				await get().fetchStatistics()

				toast.success('Task assigned successfully')
				return true
			} else {
				throw new Error(response.error || 'Failed to assign task')
			}
		} catch (error) {
			console.error('Error assigning task:', error)
			set({ error: error.message, loading: false })
			toast.error(error.message || 'Failed to assign task')
			return false
		}
	},

	// Actions - Set Filters
	setFilters: (filters) => {
		set((state) => ({
			filters: { ...state.filters, ...filters },
		}))
	},

	// Actions - Reset Filters
	resetFilters: () => {
		set({
			filters: {
				search: '',
				project_id: null,
				workflow_status: 'all',
				sortBy: 'due_date',
				sortOrder: 'ASC',
			},
		})
	},

	// Actions - Set Pagination
	setPage: (page) => {
		set((state) => ({
			pagination: { ...state.pagination, page },
		}))
	},

	setApprovalsPage: (page) => {
		set((state) => ({
			approvalsPagination: { ...state.approvalsPagination, page },
		}))
	},

	setAllTasksPage: (page) => {
		set((state) => ({
			allTasksPagination: { ...state.allTasksPagination, page },
		}))
	},

	// Actions - Clear Selected Task
	clearSelectedTask: () => {
		set({ selectedTask: null })
	},

	// Actions - Clear Error
	clearError: () => {
		set({ error: null })
	},

	// Actions - Reset Store
	reset: () => {
		set({
			myTasks: [],
			myApprovals: [],
			allTasks: [],
			selectedTask: null,
			loading: false,
			error: null,
			statistics: {
				not_started: 0,
				pending_assignment: 0,
				pending: 0,
				in_progress: 0,
				pending_review: 0,
				completed: 0,
				rejected: 0,
				overdue: 0,
				total: 0,
			},
			filters: {
				search: '',
				project_id: null,
				workflow_status: 'all',
				sortBy: 'due_date',
				sortOrder: 'ASC',
			},
			pagination: {
				page: 1,
				limit: 10,
				total: 0,
				pages: 0,
			},
			approvalsPagination: {
				page: 1,
				limit: 10,
				total: 0,
				pages: 0,
			},
			allTasksPagination: {
				page: 1,
				limit: 10,
				total: 0,
				pages: 0,
			},
		})
	},
}))

export default useTaskStore
